import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store';
import { motion } from 'framer-motion';
import {
  CurrencyDollarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon,
  PlusCircleIcon,
  BellIcon,
  EyeIcon,
  HeartIcon,
  ChatBubbleLeftIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { analyticsService } from '../../../services/analyticsService';

interface DashboardStats {
  totalPatrons: number;
  monthlyEarnings: number;
  totalPosts: number;
  engagementRate: number;
  growthRate: number;
}

interface RecentActivity {
  id: string;
  type: 'patron' | 'post' | 'earnings';
  message: string;
  timestamp: string;
  amount?: number;
}

interface TopPatron {
  id: string;
  name: string;
  avatar?: string;
  totalSpent: number;
  tier: string;
  since: string;
}

const CreatorDashboard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [stats, setStats] = useState<DashboardStats>({
    totalPatrons: 0,
    monthlyEarnings: 0,
    totalPosts: 0,
    engagementRate: 0,
    growthRate: 0,
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [topPatrons, setTopPatrons] = useState<TopPatron[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'year'>('month');

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      try {
        // Fetch comprehensive analytics for the signed-in creator
        const creatorId = user?.id as string;
        if (creatorId) {
          const { analytics } = await analyticsService.getAnalytics(creatorId, 30);
          setStats(prev => ({
            ...prev,
            totalPatrons: analytics.summary?.activePatrons ?? prev.totalPatrons,
            monthlyEarnings: analytics.summary?.monthlyRecurringRevenue ?? prev.monthlyEarnings,
            // totalPosts not provided by this endpoint; keep previous or wire later from posts API
          }));
        }

        // Keep recent activity and top patrons mocked for now
        setRecentActivity([
          {
            id: '1',
            type: 'patron',
            message: 'Sarah Johnson joined at the Gold tier',
            timestamp: '2 hours ago',
            amount: 29.99,
          },
          {
            id: '2',
            type: 'earnings',
            message: 'Monthly payment from 23 patrons',
            timestamp: '5 hours ago',
            amount: 689.97,
          },
          {
            id: '3',
            type: 'post',
            message: 'Your new post received 124 likes',
            timestamp: '1 day ago',
          },
          {
            id: '4',
            type: 'patron',
            message: 'Mike Chen upgraded to Platinum tier',
            timestamp: '2 days ago',
            amount: 49.99,
          },
          {
            id: '5',
            type: 'earnings',
            message: 'Shop sale: "Digital Art Pack"',
            timestamp: '3 days ago',
            amount: 19.99,
          },
        ]);

        setTopPatrons([
          {
            id: '1',
            name: 'Emily Rodriguez',
            avatar: undefined,
            totalSpent: 894.50,
            tier: 'Platinum',
            since: 'Jan 2023',
          },
          {
            id: '2',
            name: 'David Kim',
            avatar: undefined,
            totalSpent: 654.00,
            tier: 'Gold',
            since: 'Mar 2023',
          },
          {
            id: '3',
            name: 'Lisa Thompson',
            avatar: undefined,
            totalSpent: 432.00,
            tier: 'Silver',
            since: 'May 2023',
          },
        ]);
      } catch (e) {
        console.warn('Failed to load analytics for creator dashboard', e);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [timeRange]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'patron':
        return <UserGroupIcon className="w-5 h-5 text-green-600" />;
      case 'earnings':
        return <CurrencyDollarIcon className="w-5 h-5" style={{ color: 'var(--patreon-orange)' }} />;
      case 'post':
        return <DocumentTextIcon className="w-5 h-5 text-blue-600" />;
      default:
        return <BellIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {user?.firstName || 'Creator'}! 👋
          </h1>
          <p className="text-gray-600 mt-2">
            Here's what's happening with your Patreon page today.
          </p>
        </div>
        <button
          onClick={async () => {
            try {
              const creatorId = user?.id;
              if (!creatorId) return;
              
              const token = localStorage.getItem('accessToken');
              const response = await fetch(
                `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402'}/api/analytics/creator/${creatorId}/export/csv`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );
              
              if (!response.ok) throw new Error('Export failed');
              
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `analytics-${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            } catch (error) {
              console.error('Failed to export analytics:', error);
              alert('Failed to export analytics. Please try again.');
            }
          }}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export CSV
        </button>
      </div>

      {/* Time Range Selector */}
      <div className="mb-6">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setTimeRange('week')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              timeRange === 'week'
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Last Week
          </button>
          <button
            onClick={() => setTimeRange('month')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              timeRange === 'month'
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Last Month
          </button>
          <button
            onClick={() => setTimeRange('year')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              timeRange === 'year'
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Last Year
          </button>
        </div>
      </div>

      {/* Stats Grid - Modern Glassmorphism Design */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="relative bg-gradient-to-br from-orange-500 to-pink-600 rounded-2xl shadow-xl p-6 overflow-hidden group hover:shadow-2xl transition-shadow duration-300"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <UserGroupIcon className="w-6 h-6 text-white" />
              </div>
              <div className="flex items-center space-x-1 text-white/90 text-sm font-semibold bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                <ArrowTrendingUpIcon className="w-4 h-4" />
                <span>{stats.growthRate}%</span>
              </div>
            </div>
            <h3 className="text-3xl font-bold text-white mb-1">{stats.totalPatrons.toLocaleString()}</h3>
            <p className="text-white/80 text-sm font-medium">Total Patrons</p>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="relative bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-xl p-6 overflow-hidden group hover:shadow-2xl transition-shadow duration-300"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <CurrencyDollarIcon className="w-6 h-6 text-white" />
              </div>
              <div className="flex items-center space-x-1 text-white/90 text-sm font-semibold bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                <ArrowTrendingUpIcon className="w-4 h-4" />
                <span>+8.2%</span>
              </div>
            </div>
            <h3 className="text-3xl font-bold text-white mb-1">{formatCurrency(stats.monthlyEarnings)}</h3>
            <p className="text-white/80 text-sm font-medium">Monthly Earnings</p>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="relative bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl p-6 overflow-hidden group hover:shadow-2xl transition-shadow duration-300"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <DocumentTextIcon className="w-6 h-6 text-white" />
              </div>
              <div className="text-white/90 text-sm font-semibold bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                +12 this month
              </div>
            </div>
            <h3 className="text-3xl font-bold text-white mb-1">{stats.totalPosts}</h3>
            <p className="text-white/80 text-sm font-medium">Total Posts</p>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="relative bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-xl p-6 overflow-hidden group hover:shadow-2xl transition-shadow duration-300"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <ChartBarIcon className="w-6 h-6 text-white" />
              </div>
              <div className="flex items-center space-x-1 text-white/90 text-sm font-semibold bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                <ArrowTrendingUpIcon className="w-4 h-4" />
                <span>+3.1%</span>
              </div>
            </div>
            <h3 className="text-3xl font-bold text-white mb-1">{stats.engagementRate}%</h3>
            <p className="text-white/80 text-sm font-medium">Engagement Rate</p>
          </div>
        </motion.div>
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.4 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all"
        >
          <div className="flex items-center space-x-4">
            <div className="p-4 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl">
              <EyeIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">12.4K</p>
              <p className="text-sm text-gray-600">Page Views</p>
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-green-600 font-medium">
            <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
            <span>+15.3% from last month</span>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.5 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all"
        >
          <div className="flex items-center space-x-4">
            <div className="p-4 bg-gradient-to-br from-pink-100 to-pink-200 rounded-xl">
              <HeartIcon className="w-6 h-6 text-pink-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">1.8K</p>
              <p className="text-sm text-gray-600">Total Likes</p>
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-green-600 font-medium">
            <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
            <span>+22.1% from last month</span>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.6 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all"
        >
          <div className="flex items-center space-x-4">
            <div className="p-4 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl">
              <ChatBubbleLeftIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">342</p>
              <p className="text-sm text-gray-600">Comments</p>
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-red-600 font-medium">
            <ArrowTrendingDownIcon className="w-4 h-4 mr-1" />
            <span>-2.4% from last month</span>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions - Enhanced */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.7 }}
        className="relative rounded-2xl p-8 mb-8 overflow-hidden shadow-xl group"
        style={{ background: 'linear-gradient(135deg, #FF424D 0%, #FF6B35 50%, #F7931E 100%)' }}
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 group-hover:scale-110 transition-transform duration-500"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24 group-hover:scale-110 transition-transform duration-500"></div>
        
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center space-x-4">
            <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
              <SparklesIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">Create Something Amazing</h2>
              <p className="text-white/90 text-sm">Share exclusive content with your patrons and grow your community</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button className="bg-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all flex items-center" style={{ color: 'var(--patreon-orange)' }}>
              <PlusCircleIcon className="w-5 h-5 mr-2" />
              New Post
            </button>
            <button className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-xl font-semibold text-white hover:bg-white/30 transition-all">
              Upload Media
            </button>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-900">{activity.message}</p>
                      <div className="flex items-center mt-2 text-sm text-gray-500">
                        <ClockIcon className="w-4 h-4 mr-1" />
                        {activity.timestamp}
                        {activity.amount && (
                          <span className="ml-3 font-medium text-green-600">
                            +{formatCurrency(activity.amount)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-gray-200">
              <button className="w-full text-center font-medium" style={{ color: 'var(--patreon-orange)' }}>
                View All Activity
              </button>
            </div>
          </div>
        </div>

        {/* Top Patrons */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Top Patrons</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {topPatrons.map((patron, index) => (
                <div key={patron.id} className="p-6">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold patreon-primary-gradient" style={{ background: 'var(--patreon-btn-primary-bg)' }}>
                        {patron.name.charAt(0)}
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{patron.name}</p>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(255, 107, 53, 0.1)', color: 'var(--patreon-orange-darker)' }}>
                          {patron.tier}
                        </span>
                        <span>• {patron.since}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{formatCurrency(patron.totalSpent)}</p>
                      <p className="text-xs text-gray-500">total</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-gray-200">
              <button className="w-full text-center font-medium" style={{ color: 'var(--patreon-orange)' }}>
                View All Patrons
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorDashboard;
